// Shared artifact download helper.
//
// The download endpoint decides the real payload: single-file artifacts come
// back as-is, but directory artifacts (webapps) are zipped server-side and
// named via Content-Disposition (e.g. webapp.zip). Naming the saved file from
// artifact.fileName would label that zip "index.html" — so the header wins.

import type { ArtifactInfo } from './types.js'

/**
 * Extract the filename from a Content-Disposition header value.
 * Prefers the RFC 5987 `filename*=charset''percent-encoded` form, then the
 * plain `filename=` form (quoted or bare). Returns null when absent.
 */
export function filenameFromContentDisposition(header: string | null): string | null {
  if (!header) return null
  const extended = /filename\*=(?:UTF-8|utf-8)''([^;]+)/.exec(header)
  if (extended) {
    try {
      return decodeURIComponent(extended[1].trim())
    } catch {
      // fall through to the plain form
    }
  }
  const plain = /filename="([^"]+)"|filename=([^;\s]+)/.exec(header)
  if (plain) return (plain[1] ?? plain[2]).trim()
  return null
}

/** Canonical download URL for an artifact (DB id endpoint, else live workspace). */
export function artifactDownloadUrl(artifact: ArtifactInfo, sessionId: string, apiBaseUrl: string): string {
  if (artifact.id) return `${apiBaseUrl}/agent/artifacts/${artifact.id}/download`
  return `${apiBaseUrl}/agent/session/${sessionId}/workspace/files/${artifact.filePath.replace(/^\//, '')}`
}

/**
 * Fetch an artifact and trigger a browser save-as download, named from the
 * response's Content-Disposition (falling back to artifact.fileName).
 * Rejects on non-OK responses instead of saving the error body as a file.
 */
export async function triggerArtifactDownload({ artifact, sessionId, apiBaseUrl = '', authHeader }: {
  artifact: ArtifactInfo
  sessionId: string
  apiBaseUrl?: string
  authHeader?: string
}): Promise<void> {
  const headers: Record<string, string> = {}
  if (authHeader) headers['Authorization'] = authHeader

  const res = await fetch(artifactDownloadUrl(artifact, sessionId, apiBaseUrl), { headers })
  if (!res.ok) throw new Error(`HTTP ${res.status}`)

  const fileName = filenameFromContentDisposition(res.headers.get('Content-Disposition')) || artifact.fileName
  const blob = await res.blob()
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = fileName
  a.click()
  URL.revokeObjectURL(url)
}
